import type { UnknownAction } from '@reduxjs/toolkit';
import { isEqual, isNil } from 'lodash-es';

import { isForbiddenError } from '../../services/errors/errors';
import { log } from '../../services/logging';
import { errorMapper } from '../../services/queries/errorMapper';
import type { Scene } from '../../types/scene';
import type { Style } from '../../types/style';
import {
  getUserId,
  isPerspectiveSharingAvailable,
  selectUserCanSaveScene,
  setDatabase,
} from '../connections/connectionsDuck';
import type { Persistor } from '../persistence/types';
import { getPerspective } from '../perspectives/perspectives.selector';
import { unsetCurrentPerspective } from '../rootReducer';
import { isSharedStorageAvailable } from '../shared-storage/shared-storage.api';
import type { store as RootStore } from '../store';
import { STYLE_TYPE_SCENE } from '../styles/styles.const';
import { selectCurrentStyle } from '../styles/styles.selectors';
import type { AppDispatch, RootState } from '../types';
import {
  duplicateScene,
  setEditMode,
  updateSceneGraphCounters,
  updateSceneName,
  updateSceneRoles,
  updateTemporarySceneData,
} from './scene';
import { SceneTransformer } from './scene-transformer';
import {
  SCENE_EDIT_MODE_EDITING,
  getCurrentScene,
  getSceneById,
  getSceneEditMode,
  getSceneLoadingStatus,
  isSceneStoring,
} from './scene.selectors';
import { applySelectedSceneThunk } from './thunks/apply-selected-scene';
import { storeSceneThunk } from './thunks/store-scene';
import { updateSceneThunk } from './thunks/update-scene';

export class SharedStorageScenePersistor implements Persistor {
  private readonly sceneTransformer: SceneTransformer;

  private previousScene: Partial<Scene>;

  constructor() {
    this.sceneTransformer = new SceneTransformer();
    this.previousScene = {};
  }

  isEligible(state: RootState): boolean {
    const isPluginPresent = Boolean(state?.connections?.procedures && isPerspectiveSharingAvailable(state));
    return !isPluginPresent && isSharedStorageAvailable();
  }

  shouldLoadState({ action, state }: { action: UnknownAction | null; state: RootState }): boolean {
    if (!this.isEligible(state)) return false;

    return !action || setDatabase.match(action);
  }

  async loadFromStorage({
    store,
    payload,
    action,
  }: {
    store: typeof RootStore;
    payload: object;
    action: UnknownAction;
  }): Promise<object | void> {
    const { dispatch, getState } = store;

    const state = getState();

    const isFirstLogin = isNil(getPerspective(state));

    if (isNil(action)) {
      if (!isFirstLogin) {
        dispatch(unsetCurrentPerspective());
      }
    }

    return payload;
  }

  shouldSaveState({ state, action }: { state: RootState; action: UnknownAction | null }): boolean {
    if (!this.isEligible(state)) return false;

    const sceneDetails = !isNil(state.scene) ? getCurrentScene(state) : null;
    const userCanSaveScenePrivilege = selectUserCanSaveScene(state);
    const userIsSceneOwner = sceneDetails?.createdBy === getUserId(state);
    const userCanEdit = !isNil(state.scene) ? getSceneEditMode(state) === SCENE_EDIT_MODE_EDITING : false;
    const hasDatabaseChanged = !isNil(action) && setDatabase.match(action);
    const isTemporaryScene = !isNil(sceneDetails?.temporaryData);

    if (hasDatabaseChanged) {
      return false;
    }

    if (!userCanSaveScenePrivilege && !isTemporaryScene) {
      return false;
    }

    if (sceneDetails && isSceneStoring(state, sceneDetails.id)) {
      return false;
    }

    if (!isNil(action) && duplicateScene.match(action)) {
      return true;
    }

    if (!userCanEdit) {
      return false;
    }

    if (action?.type === updateSceneName.toString()) {
      return true;
    }

    if (action?.type === updateSceneRoles.toString()) {
      return true;
    }

    if (userIsSceneOwner && getSceneLoadingStatus(state) === 'idle') {
      const newFullScene = this.sceneTransformer.getFullSceneFromCurrentGraph(state, sceneDetails);
      const newSceneForComparison = this.sceneTransformer.keepPropertiesThatCanTriggerSceneSaving(newFullScene);
      const isSceneEqual = isEqual(this.previousScene, newSceneForComparison);
      return !isSceneEqual;
    }

    return false;
  }

  async saveToStorage({
    state,
    dispatch,
    action,
  }: {
    state: RootState;
    dispatch: AppDispatch;
    action: UnknownAction;
  }): Promise<void> {
    if (duplicateScene.match(action)) {
      await this.saveDuplicateScene(state, dispatch, action.payload);
      return;
    }

    if ((updateSceneName.match(action) || updateSceneRoles.match(action)) && !isNil(action.payload.sceneId)) {
      await this.saveOnlySceneDetails(state, dispatch, action.payload.sceneId);
      return;
    }

    const sceneDetails = getCurrentScene(state);
    const isTemporaryScene = Boolean(sceneDetails?.temporaryData);
    const isNewScene = isNil(sceneDetails?.lastModified);

    if (isTemporaryScene) {
      await this.saveTemporaryScene(state, dispatch);
    } else if (isNewScene) {
      await this.saveNewScene(state, dispatch);
    } else {
      await this.saveCurrentScene(state, dispatch);
    }
  }

  async saveDuplicateScene(state: RootState, dispatch: AppDispatch, scene: Scene): Promise<void> {
    const currentStyle: Partial<Style> = {
      ...selectCurrentStyle(state),
      id: scene.id,
      type: STYLE_TYPE_SCENE,
    };
    const sceneToStore = this.sceneTransformer.getFullSceneFromCurrentGraph(state, scene, currentStyle);

    try {
      await dispatch(updateSceneThunk({ sceneId: sceneToStore.id, update: sceneToStore }));
      dispatch(setEditMode(SCENE_EDIT_MODE_EDITING));
      await dispatch(applySelectedSceneThunk({ sceneId: scene.id }));
    } catch (e) {
      const error = errorMapper(e);
      if (isForbiddenError(error)) {
        log.info('User is not allowed to update Scene');
        log.debug(error);
      } else {
        log.error('Error while duplicating scene');
        log.error(error);
      }
    }
  }

  async saveOnlySceneDetails(state: RootState, dispatch: AppDispatch, sceneId: Scene['id']): Promise<void> {
    const sceneDetails = getSceneById(state)(sceneId);
    if (sceneDetails === undefined) {
      throw new Error('Scene not found');
    }

    try {
      await dispatch(updateSceneThunk({ sceneId: sceneDetails.id, update: sceneDetails }));
    } catch (e) {
      const error = errorMapper(e);
      if (isForbiddenError(error)) {
        log.info('User is not allowed to update Scene');
        log.debug(error);
      } else {
        log.error('Error while saving scene');
        log.error(error);
      }
    }
  }

  async saveTemporaryScene(state: RootState, dispatch: AppDispatch): Promise<void> {
    const perspective = getPerspective(state);
    if (!perspective) return;
    const sceneDetails = getCurrentScene(state);
    const fullScene = this.sceneTransformer.getFullSceneFromCurrentGraph(state, sceneDetails);
    fullScene.perspectiveId = perspective.id;

    dispatch(updateTemporarySceneData({ sceneId: fullScene.id, temporaryData: fullScene }));
    dispatch(
      updateSceneGraphCounters({
        numOfNodes: fullScene.numOfNodes,
        numOfRels: fullScene.numOfRels,
      }),
    );

    this.previousScene = this.sceneTransformer.keepPropertiesThatCanTriggerSceneSaving(fullScene);
    delete this.previousScene.perspectiveId;
  }

  async saveNewScene(state: RootState, dispatch: AppDispatch): Promise<void> {
    const perspective = getPerspective(state);
    const sceneDetails = getCurrentScene(state);
    const fullScene = this.sceneTransformer.getFullSceneFromCurrentGraph(state, sceneDetails);
    const sceneToStore = { ...fullScene };

    if (!perspective) {
      throw new Error('Perspective not found');
    }

    try {
      await dispatch(storeSceneThunk({ ...sceneToStore, perspectiveId: perspective.id }));
    } catch (e) {
      log.error('Error while saving scene', e);
    }
  }

  async saveCurrentScene(state: RootState, dispatch: AppDispatch): Promise<void> {
    const sceneDetails = getCurrentScene(state);
    const fullScene = this.sceneTransformer.getFullSceneFromCurrentGraph(state, sceneDetails);
    const sceneToStore = { ...fullScene };

    try {
      if (isEqual(this.previousScene.nodes, sceneToStore.nodes)) {
        delete sceneToStore.nodes;
        delete sceneToStore.relationships;

        if (this.previousScene.visualisation === sceneToStore.visualisation) {
          delete sceneToStore.visualisation;
        }
        sceneToStore.numOfRels = sceneDetails?.numOfRels ?? 0;
      }

      if (this.previousScene.filters === sceneToStore.filters) {
        delete sceneToStore.filters;
      }

      if (this.previousScene.ranges === sceneToStore.ranges) {
        delete sceneToStore.ranges;
      }

      if (this.previousScene.gds === sceneToStore.gds) {
        delete sceneToStore.gds;
      }

      if (sceneToStore.style != null && isEqual(this.previousScene.style, sceneToStore.style)) {
        delete sceneToStore.style;
      }

      await dispatch(updateSceneThunk({ sceneId: sceneToStore.id, update: sceneToStore }));
      this.previousScene = this.sceneTransformer.keepPropertiesThatCanTriggerSceneSaving(fullScene);
    } catch (e) {
      const error = errorMapper(e);
      if (isForbiddenError(error)) {
        log.info('User is not allowed to update scene');
        log.debug(error);
      } else {
        log.error('Error while saving scene');
        log.error(error);
      }
    }
  }
}
